/**
 * Pure SSE frame parser shared by every streaming endpoint.
 *
 * Given a ReadableStream of UTF-8 text, yields `{ event, data }` per
 * frame. Handles CRLF normalisation, multi-line `data:` fields, and
 * silently drops malformed JSON (so a single bad frame doesn't kill
 * the iterator).
 *
 * Not tied to any specific endpoint — `streamScan.ts` and
 * `streamPrimaryPhoto.ts` both layer their per-endpoint auth/URL
 * setup around this.
 *
 * Frame grammar (SSE spec):
 *   event: <name>\n
 *   data: <line 1>\n
 *   data: <line 2>\n
 *   \n                ← blank line = end of frame
 *
 * If no `event:` line is present, the default name is `message`.
 * Multi-line `data:` values are concatenated with `\n` then parsed
 * as JSON.
 */

/** Raw frame surfaced to the caller. `event` is the SSE event name
 *  (defaults to `'message'`); `data` is the JSON.parse result. */
export interface SseFrame<TData = unknown> {
  event: string;
  data: TData;
}

/**
 * Asynchronously iterate SSE frames from a UTF-8 ReadableStream.
 *
 * `stream` must already be text-decoded — the caller is expected to
 * have done `response.body.pipeThrough(new TextDecoderStream())`
 * upstream. Keeping the decoder outside this module makes the parser
 * testable without a TextDecoderStream polyfill.
 */
export async function* parseSseStream<TData = unknown>(
  stream: ReadableStream<string>,
): AsyncGenerator<SseFrame<TData>, void, void> {
  const reader = stream.getReader();
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      if (!value) continue;
      buffer += value;
      // Normalise CRLF to LF so the \n\n frame boundary always hits.
      buffer = buffer.replace(/\r\n/g, '\n');

      let sepIdx: number;
      while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
        const raw = buffer.slice(0, sepIdx);
        buffer = buffer.slice(sepIdx + 2);

        const frame = parseSseFrame<TData>(raw);
        if (frame) yield frame;
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* noop */
    }
  }
}

/**
 * Parse a single SSE frame's text into `{event, data}`. Returns
 * `null` for frames that have no `data:` lines or whose data isn't
 * valid JSON — the caller should skip these (the server should
 * never emit them; this is defence in depth).
 *
 * Exported so unit tests can exercise the frame-level behaviour
 * directly without bootstrapping a ReadableStream.
 */
export function parseSseFrame<TData = unknown>(
  raw: string,
): SseFrame<TData> | null {
  let eventName = 'message';
  const dataLines: string[] = [];
  for (const line of raw.split('\n')) {
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim());
    }
    // Other SSE fields (`id:`, `retry:`) are ignored — we don't use
    // Last-Event-ID reconnection.
  }
  if (dataLines.length === 0) return null;

  try {
    return { event: eventName, data: JSON.parse(dataLines.join('\n')) as TData };
  } catch {
    return null;
  }
}
